'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Input';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { Pill, PillGroup } from '@/components/Pill';
import { getClient } from '@/lib/api';
import { eur, shortDate } from '@/lib/fmt';
import { useFxRates, type FxRates } from '@/lib/fx';
import { getAccessToken } from '@/lib/auth';
import { getApiUrl } from '@/lib/settings';
import type { components } from '@/lib/api.types';

type ImportResult = components['schemas']['ImportResponse'];
type Aspsp = components['schemas']['AspspOut'];
type BankConn = components['schemas']['BankConnectionOut'];
type DailyRefresh = components['schemas']['DailyRefreshResponse'];
type StoredBalance = components['schemas']['StoredBalanceOut'];
type Holding = components['schemas']['HoldingOut'];
type SyncResult = components['schemas']['SyncResponse'];

type CsvSource = 'novobanco' | 'revolut' | 'paypal';

type AccountKey = string; // `${source}:${account_id}`

function accountKey(b: { source: string; account_id: string }): AccountKey {
  return `${b.source}:${b.account_id}`;
}

export default function ImportsPage() {
  return (
    <NeedsAuth>
      <Inner />
    </NeedsAuth>
  );
}

function Inner() {
  const [tab, setTab] = useState<'bank' | 'csv'>('bank');

  // --- balances + daily refresh state, lifted to the top so the hero can
  // share it with the rest of the page (and so a refresh re-fetches without
  // child remounting). One source of truth for "what does the user own".
  const [balances, setBalances] = useState<StoredBalance[] | null>(null);
  const [holdings, setHoldings] = useState<Holding[] | null>(null);
  const [balErr, setBalErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AccountKey | 'all'>('all');
  const [tick, setTick] = useState(0);

  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshResult, setRefreshResult] = useState<DailyRefresh | null>(null);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);

  // FX rates (EUR-base). We need them to convert non-EUR account balances
  // to EUR for the combined total — holdings already arrive in EUR from
  // the backend.
  const { rates: fxRates } = useFxRates();

  useEffect(() => {
    let cancelled = false;
    const c = getClient();
    if (!c) return;
    void (async () => {
      // Trigger a holdings price refresh up front so the combined-balance
      // total reflects current market value, not stale prices. The
      // backend short-circuits on its own TTL — calling it on every load
      // is intentional and cheap.
      await c.POST('/api/holdings/refresh-prices', {});
      if (cancelled) return;
      const [bRes, hRes] = await Promise.all([
        c.GET('/api/balances/latest'),
        c.GET('/api/holdings'),
      ]);
      if (cancelled) return;
      if (bRes.error) setBalErr(formatErr(bRes.error));
      else {
        setBalances(bRes.data?.balances ?? []);
        setBalErr(null);
      }
      if (!hRes.error) setHoldings(hRes.data?.holdings ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  async function runRefresh() {
    const c = getClient();
    if (!c) return;
    setRefreshBusy(true);
    setRefreshErr(null);
    setRefreshResult(null);
    try {
      const { data, error, response } = await c.POST('/api/sync/refresh-daily', {
        params: { query: { days: 2 } },
      });
      if (error) setRefreshErr(`${response.status}: ${formatErr(error)}`);
      else {
        setRefreshResult(data ?? null);
        setTick((t) => t + 1);
      }
    } finally {
      setRefreshBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageTitle
        actions={
          <Button variant="primary" onClick={runRefresh} loading={refreshBusy}>
            Run daily refresh
          </Button>
        }
      >
        Imports
      </PageTitle>

      <AccountsHero
        balances={balances}
        holdings={holdings}
        fxRates={fxRates}
        balErr={balErr}
        selected={selected}
        onSelect={setSelected}
      />

      <RefreshSummary result={refreshResult} err={refreshErr} />

      <Tabs current={tab} onChange={setTab} />

      {tab === 'bank' ? (
        <>
          <BankConnectionsCard />
          <ConsentManagementCard />
        </>
      ) : (
        <CsvImportCard />
      )}

      <p className="text-xs text-(--color-muted)">
        API URL in use: <code className="font-mono">{getApiUrl()}</code>
      </p>
    </div>
  );
}

// --- accounts hero (combined total + per-account chip switcher) ----------

function AccountsHero({
  balances,
  holdings,
  fxRates,
  balErr,
  selected,
  onSelect,
}: {
  balances: StoredBalance[] | null;
  holdings: Holding[] | null;
  fxRates: FxRates | null;
  balErr: string | null;
  selected: AccountKey | 'all';
  onSelect: (key: AccountKey | 'all') => void;
}) {
  // Sum across whichever subset is selected. Everything is normalized to
  // EUR via the FX rates: native_amount * (1 / rates_eur_base[currency])
  // — i.e. divide by "currency per EUR".
  const filtered = useMemo(() => {
    if (!balances) return [];
    if (selected === 'all') return balances;
    return balances.filter((b) => accountKey(b) === selected);
  }, [balances, selected]);

  // Convert any account balance to EUR. Unknown currency (or rates not
  // loaded yet) → treat as EUR pass-through, which is honest if a bit
  // imprecise and gets corrected once rates arrive.
  function toEur(amount: number, currency: string): number {
    if (currency === 'EUR') return amount;
    const rate = fxRates?.[currency.toUpperCase()];
    if (!rate || rate <= 0) return amount;
    return amount / rate;
  }

  const accountsEur = filtered.reduce(
    (acc, b) => acc + toEur(Number(b.amount), b.currency),
    0,
  );

  // Holdings only contribute when "all accounts" is selected — there's no
  // notion of "this holding belongs to bank X". Holdings are stored in
  // EUR by the backend, so no FX hop here.
  const showHoldings = selected === 'all' && holdings && holdings.length > 0;
  const holdingsEur = showHoldings
    ? holdings!
        .filter((h) => h.value !== null && h.value !== undefined)
        .reduce((acc, h) => acc + Number(h.value), 0)
    : 0;
  const nonEurAccounts = filtered.filter((b) => b.currency !== 'EUR');

  const eurTotal = accountsEur + holdingsEur;

  // One chip per unique account; "All" first.
  const chips: { key: AccountKey | 'all'; label: string; sub: string }[] =
    useMemo(() => {
      const balCount = balances?.length ?? 0;
      const holdCount = holdings?.length ?? 0;
      const allSub = (() => {
        const parts: string[] = [];
        if (balCount > 0)
          parts.push(`${balCount} balance${balCount === 1 ? '' : 's'}`);
        if (holdCount > 0)
          parts.push(`${holdCount} holding${holdCount === 1 ? '' : 's'}`);
        return parts.length ? parts.join(' · ') : '—';
      })();
      const out: { key: AccountKey | 'all'; label: string; sub: string }[] = [
        { key: 'all', label: 'All accounts', sub: allSub },
      ];
      if (!balances) return out;
      for (const b of balances) {
        out.push({
          key: accountKey(b),
          label: b.account_name || `${b.account_id.slice(0, 8)}…`,
          sub: b.source,
        });
      }
      return out;
    }, [balances, holdings]);

  // Latest captured-at across the displayed subset — gives the user a
  // "freshness" signal without having to dig into a row.
  const lastCapturedAt = filtered.reduce<string | null>((latest, b) => {
    if (!latest) return b.captured_at;
    return new Date(b.captured_at) > new Date(latest) ? b.captured_at : latest;
  }, null);

  return (
    <Card>
      <div className="flex flex-col gap-5">
        {balErr && (
          <p className="text-xs text-(--color-negative)">
            Could not load balances: {balErr}
          </p>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-(--color-muted)">
            {selected === 'all' ? 'Combined balance' : 'Account balance'}
          </span>
          <span className="text-4xl font-semibold tabular-nums">
            {balances === null ? '…' : eur(eurTotal)}
          </span>
          {showHoldings && holdingsEur > 0 && (
            <span className="text-xs text-(--color-muted)">
              includes {eur(holdingsEur)} in holdings
            </span>
          )}
          {nonEurAccounts.length > 0 && (
            <span className="text-xs text-(--color-muted)">
              FX-converted from{' '}
              {nonEurAccounts.map((b) => `${b.amount} ${b.currency}`).join(', ')}
            </span>
          )}
          {lastCapturedAt && (
            <span className="text-xs text-(--color-muted)">
              as of {shortDate(lastCapturedAt)}
            </span>
          )}
          {balances !== null && balances.length === 0 && !showHoldings && (
            <span className="text-xs text-(--color-muted)">
              No balances yet — connect a bank or run the daily refresh.
            </span>
          )}
        </div>

        {balances && balances.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => {
              const active = c.key === selected;
              return (
                <button
                  key={c.key}
                  onClick={() => onSelect(c.key)}
                  className={
                    'pressable rounded-full px-3 py-1.5 text-sm transition-shadow ' +
                    (active
                      ? 'bg-hero-wash shadow-sm'
                      : 'bg-sunken text-fg-muted hover:bg-raised hover:text-fg-primary')
                  }
                  style={active ? { color: 'var(--hero-start)' } : undefined}
                >
                  <span className="font-medium">{c.label}</span>
                  <span className="ml-2 text-xs opacity-70">{c.sub}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

function RefreshSummary({
  result,
  err,
}: {
  result: DailyRefresh | null;
  err: string | null;
}) {
  if (err) {
    return (
      <Card>
        <p className="text-sm text-(--color-negative)">Refresh failed: {err}</p>
      </Card>
    );
  }
  if (!result) return null;

  const txInserted = result.sync_outcomes.reduce((a, o) => a + o.inserted, 0);
  const txUpdated = result.sync_outcomes.reduce((a, o) => a + o.updated, 0);
  const balCaptured = result.balance_outcomes.reduce(
    (a, b) => a + b.captured,
    0,
  );
  const errors = result.sync_outcomes.filter((o) => o.error && !o.skipped);

  return (
    <Card>
      <div className="text-xs text-(--color-muted) flex flex-col gap-1">
        <span>
          <span className="text-(--color-positive)">{txInserted}</span> new
          transactions · <span>{txUpdated}</span> refreshed ·{' '}
          <span className="text-(--color-accent)">{balCaptured}</span> balance
          snapshots
        </span>
        {result.categorize && (
          <span>
            Categorized {result.categorize.examined} ·{' '}
            <span className="text-(--color-positive)">
              {result.categorize.by_rule} by rule
            </span>{' '}
            ·{' '}
            <span className="text-(--color-accent)">
              {result.categorize.by_ai} by AI
            </span>
            {result.categorize.skipped_no_ai > 0 && (
              <>
                {' '}
                ·{' '}
                <span className="text-(--color-negative)">
                  {result.categorize.skipped_no_ai} skipped (no AI key / CLI)
                </span>
              </>
            )}
          </span>
        )}
        {errors.map((o) => (
          <span key={o.source} className="text-(--color-negative)">
            {o.source}: {o.error}
          </span>
        ))}
      </div>
    </Card>
  );
}

// --- tabs (Connect a bank | Upload CSV) ----------------------------------

function Tabs({
  current,
  onChange,
}: {
  current: 'bank' | 'csv';
  onChange: (t: 'bank' | 'csv') => void;
}) {
  const tabs: { key: 'bank' | 'csv'; label: string; sub: string }[] = [
    { key: 'bank', label: 'Connect a bank', sub: 'Enable Banking' },
    { key: 'csv', label: 'Upload CSV', sub: 'fallback' },
  ];
  return (
    <PillGroup>
      {tabs.map((t) => (
        <Pill key={t.key} active={t.key === current} onClick={() => onChange(t.key)}>
          {t.label}
          <span className="ml-1.5 opacity-70">{t.sub}</span>
        </Pill>
      ))}
    </PillGroup>
  );
}

// --- Bank connections (Enable Banking link flow) -------------------------

function explainLinkError(reason: string): string {
  if (reason === 'no_accounts') {
    return 'Link failed: bank returned no accounts. If this is Revolut, try the "business" PSU type, or re-check that you selected at least one account at the bank\'s consent page.';
  }
  if (reason === 'no_session_id') {
    return 'Link failed: bank did not return a session. Try again, or try a different PSU type.';
  }
  if (reason === 'invalid_state') {
    return 'Link failed: consent expired or invalid — start the link again.';
  }
  if (reason === 'missing_code_or_state') {
    return 'Link failed: bank redirect was missing required parameters — start the link again.';
  }
  if (reason.startsWith('exchange_failed:')) {
    return `Link failed: bank rejected the consent code (${reason.slice('exchange_failed:'.length)}).`;
  }
  return `Link failed: ${reason}`;
}

type EbTerms = {
  version: string;
  hash: string;
  text: string;
  accepted_version: string | null;
  accepted_at: string | null;
  requires_acceptance: boolean;
};

function BankConnectionsCard() {
  const [conns, setConns] = useState<BankConn[] | null>(null);
  const [connsErr, setConnsErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Read-only consent gate — see /api/eb/terms. Until the user has a
  // current acceptance for the running terms version, the Connect flow
  // is hidden behind the agreement block below.
  const [terms, setTerms] = useState<EbTerms | null>(null);
  const [termsErr, setTermsErr] = useState<string | null>(null);
  const [termsBusy, setTermsBusy] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [aspsps, setAspsps] = useState<Aspsp[] | null>(null);
  const [aspspName, setAspspName] = useState<string>('');
  const [country, setCountry] = useState('PT');
  const [days, setDays] = useState(89);
  const [psuType, setPsuType] = useState<'personal' | 'business'>('personal');

  const [linkBusy, setLinkBusy] = useState(false);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  // Compute the banner from URL params during the first render — `Inner`
  // only mounts client-side (gated by NeedsAuth), so reading window.location
  // here is safe and avoids a synchronous setState in effect.
  const [linkBanner] = useState<
    { tone: 'ok' | 'err'; text: string } | null
  >(() => {
    if (typeof window === 'undefined') return null;
    const u = new URL(window.location.href);
    const linkParam = u.searchParams.get('link');
    if (!linkParam) return null;
    if (linkParam === 'ok') {
      const n = u.searchParams.get('accounts');
      return { tone: 'ok', text: `Bank linked${n ? ` — ${n} account(s)` : ''}.` };
    }
    const reason = u.searchParams.get('reason') || 'unknown error';
    return { tone: 'err', text: explainLinkError(reason) };
  });

  // Strip the callback params from the URL after the banner has been read,
  // and refetch the connections list once the backend's commit has settled.
  // The setTick is deferred to a microtask so it isn't a synchronous setState
  // in the effect body.
  const justReturnedFromLink = linkBanner !== null;
  useEffect(() => {
    if (!justReturnedFromLink) return;
    const u = new URL(window.location.href);
    u.searchParams.delete('link');
    u.searchParams.delete('accounts');
    u.searchParams.delete('reason');
    window.history.replaceState({}, '', u.toString());
    const timer = setTimeout(() => setTick((t) => t + 1), 0);
    return () => clearTimeout(timer);
  }, [justReturnedFromLink]);

  useEffect(() => {
    let cancelled = false;
    const c = getClient();
    if (!c) return;
    void (async () => {
      const { data, error } = await c.GET('/api/eb/connections');
      if (cancelled) return;
      if (error) setConnsErr(formatErr(error));
      else {
        setConns(data?.connections ?? []);
        setConnsErr(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  // Fetch the current read-only terms + acceptance state. Re-fetches on
  // `tick` so accepting/linking refreshes the gate.
  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    void (async () => {
      try {
        const resp = await fetch(`${getApiUrl()}/api/eb/terms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!resp.ok) {
          setTermsErr(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
          return;
        }
        const body = (await resp.json()) as EbTerms;
        if (cancelled) return;
        setTerms(body);
        setTermsErr(null);
        // If they've already accepted, keep the checkbox in sync so any
        // subsequent re-acceptance (after a version bump) starts unchecked.
        setTermsAgreed(!body.requires_acceptance);
      } catch (e) {
        if (!cancelled) setTermsErr(e instanceof Error ? e.message : 'fetch failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  async function acceptTerms() {
    if (!terms) return;
    const token = getAccessToken();
    if (!token) return;
    setTermsBusy(true);
    setTermsErr(null);
    try {
      const resp = await fetch(`${getApiUrl()}/api/eb/terms/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ version: terms.version, hash: terms.hash }),
      });
      if (!resp.ok) {
        setTermsErr(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
        return;
      }
      setTick((t) => t + 1);
    } catch (e) {
      setTermsErr(e instanceof Error ? e.message : 'accept failed');
    } finally {
      setTermsBusy(false);
    }
  }

  async function loadAspsps() {
    const c = getClient();
    if (!c) return;
    setLinkErr(null);
    const { data, error } = await c.GET('/api/eb/aspsps', {
      params: { query: { country } },
    });
    if (error) {
      setLinkErr(formatErr(error));
      setAspsps([]);
      return;
    }
    const list = data?.aspsps ?? [];
    setAspsps(list);
    if (list.length > 0 && !aspspName) {
      // Default to novobanco if present, else first item.
      const novo = list.find((a) => a.name === 'novobanco');
      setAspspName((novo ?? list[0]).name);
    }
  }

  async function startLink() {
    const c = getClient();
    if (!c) return;
    if (!aspspName) {
      setLinkErr('Pick a bank first.');
      return;
    }
    setLinkBusy(true);
    setLinkErr(null);
    try {
      const { data, error } = await c.POST('/api/eb/link/start', {
        body: {
          aspsp_name: aspspName,
          aspsp_country: country,
          psu_type: psuType,
          consent_days: days,
        },
      });
      if (error) {
        setLinkErr(formatErr(error));
        return;
      }
      if (!data?.auth_url) {
        setLinkErr('Backend did not return an auth URL.');
        return;
      }
      // Hand off to the bank — they'll redirect to /api/eb/callback which
      // bounces back to this page with ?link=ok|error.
      window.location.href = data.auth_url;
    } finally {
      setLinkBusy(false);
    }
  }

  async function disconnect(id: string) {
    const c = getClient();
    if (!c) return;
    if (!confirm('Disconnect this bank? You will need to re-consent to sync again.')) return;
    const { error } = await c.DELETE('/api/eb/connections/{connection_id}', {
      params: { path: { connection_id: id } },
    });
    if (error) {
      setConnsErr(formatErr(error));
      return;
    }
    setTick((t) => t + 1);
  }

  return (
    <Card title="Bank connections" hint="Enable Banking — secured & encrypted">
      <div className="flex flex-col gap-4">
        {linkBanner && (
          <p
            className={
              'text-sm ' +
              (linkBanner.tone === 'ok'
                ? 'text-(--color-positive)'
                : 'text-(--color-negative)')
            }
          >
            {linkBanner.text}
          </p>
        )}

        {connsErr && (
          <p className="text-xs text-(--color-negative)">Connections error: {connsErr}</p>
        )}

        {conns === null && !connsErr && (
          <p className="text-xs text-(--color-muted)">Loading…</p>
        )}

        {conns && conns.length === 0 && (
          <p className="text-sm text-(--color-muted)">
            No connections yet. Pick a bank below to start the consent flow.
          </p>
        )}

        {conns && conns.length > 0 && (
          <ul className="flex flex-col gap-2">
            {conns.map((c) => (
              <ConnectionRow key={c.id} conn={c} onRevoke={() => disconnect(c.id)} />
            ))}
          </ul>
        )}

        {conns && conns.some((c) => c.aspsp_name === 'novobanco') && (
          <BackfillRange />
        )}

        {terms && terms.requires_acceptance && (
          <div className="flex flex-col gap-3 rounded-md bg-sunken p-4">
            <h3 className="text-sm font-semibold text-fg-primary">
              Read-only consent (required before linking)
            </h3>
            <p className="text-xs text-(--color-muted)">
              Enable Banking is the regulated party that talks to your bank.
              Flowlet only ever asks for read access (balances + transactions)
              — never payments. The full terms are below.
            </p>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-sm bg-surface p-3 font-mono text-xs leading-relaxed text-fg-primary">
              {terms.text}
            </pre>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I have read the terms above (version{' '}
                <code className="font-mono">{terms.version}</code>) and agree
                to them, including Enable Banking&apos;s end-user terms and
                privacy notice.
              </span>
            </label>
            {termsErr && (
              <p className="text-xs text-(--color-negative)">{termsErr}</p>
            )}
            <div>
              <Button
                variant="primary"
                onClick={acceptTerms}
                disabled={!termsAgreed}
                loading={termsBusy}
              >
                Accept and continue
              </Button>
            </div>
          </div>
        )}

        {terms && !terms.requires_acceptance && (
          <p className="text-xs text-(--color-muted)">
            Read-only consent accepted (version{' '}
            <code className="font-mono">{terms.version}</code>
            {terms.accepted_at
              ? ` on ${shortDate(terms.accepted_at)}`
              : ''}
            ).
          </p>
        )}

        <div className="flex flex-col gap-3 border-t border-hairline pt-4">
          <h3 className="text-sm font-semibold text-fg-primary">Connect a new bank</h3>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-fg-muted">Country</span>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                className="!w-20 font-mono"
              />
            </label>
            <Button variant="secondary" size="sm" onClick={loadAspsps}>
              {aspsps === null ? 'Load banks' : 'Reload banks'}
            </Button>
          </div>

          {aspsps && aspsps.length > 0 && (
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-48 flex-1 flex-col gap-1 text-sm">
                <span className="text-fg-muted">Bank</span>
                <Select value={aspspName} onChange={(e) => setAspspName(e.target.value)}>
                  {aspsps.map((a) => (
                    <option key={a.name} value={a.name}>
                      {a.name}
                      {a.maximum_consent_validity_days
                        ? ` (max ${a.maximum_consent_validity_days}d)`
                        : ''}
                      {a.beta ? ' · beta' : ''}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-fg-muted">PSU</span>
                <Select
                  value={psuType}
                  onChange={(e) => setPsuType(e.target.value as 'personal' | 'business')}
                >
                  <option value="personal">personal</option>
                  <option value="business">business</option>
                </Select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-fg-muted">Days</span>
                <Input
                  type="number"
                  min={1}
                  max={180}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value) || 89)}
                  className="!w-20"
                />
              </label>
              <Button
                variant="primary"
                onClick={startLink}
                disabled={
                  !aspspName ||
                  (terms?.requires_acceptance ?? true)
                }
                loading={linkBusy}
              >
                Connect
              </Button>
            </div>
          )}

          {linkErr && <p className="text-xs text-(--color-negative)">{linkErr}</p>}

          <p className="text-xs text-(--color-muted)">
            We never see your bank credentials — you authenticate directly with the bank.
            Only an opaque session id comes back, and it&apos;s encrypted at rest.
          </p>
        </div>
      </div>
    </Card>
  );
}

// --- Consent management (history + withdraw) ----------------------------
//
// PSD2 Art. 64 / GDPR Art. 7 require the user to be able to withdraw consent
// at any time and to see what they've agreed to. The withdrawal endpoint
// cascades server-side: it revokes every active Enable Banking session in
// addition to the acceptance row, so the user doesn't have to disconnect
// each bank separately.

type EbTermsHistoryEntry = {
  id: string;
  terms_version: string;
  terms_hash: string;
  accepted_at: string;
  revoked_at: string | null;
  ip: string | null;
  user_agent: string | null;
};

type EbTermsRevokeResponse = {
  acceptances_revoked: number;
  connections_revoked: number;
  revoked_at: string;
};

function ConsentManagementCard() {
  const [history, setHistory] = useState<EbTermsHistoryEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeResult, setRevokeResult] = useState<EbTermsRevokeResponse | null>(
    null,
  );
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    void (async () => {
      try {
        const resp = await fetch(`${getApiUrl()}/api/eb/terms/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!resp.ok) {
          setErr(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
          return;
        }
        const body = (await resp.json()) as { entries: EbTermsHistoryEntry[] };
        if (cancelled) return;
        setHistory(body.entries);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'fetch failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  async function withdraw() {
    if (
      !confirm(
        'Withdraw Enable Banking consent and disconnect every linked bank?\n\n' +
          'This is immediate. You will need to re-accept the terms and re-link to ' +
          'use bank sync again.',
      )
    )
      return;
    const token = getAccessToken();
    if (!token) return;
    setRevokeBusy(true);
    setErr(null);
    setRevokeResult(null);
    try {
      const resp = await fetch(`${getApiUrl()}/api/eb/terms/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        setErr(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
        return;
      }
      setRevokeResult((await resp.json()) as EbTermsRevokeResponse);
      setTick((t) => t + 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'revoke failed');
    } finally {
      setRevokeBusy(false);
    }
  }

  const active = (history ?? []).filter((r) => r.revoked_at === null);

  return (
    <Card
      title="Consent management"
      hint="Withdraw consent or review what you've agreed to"
    >
      <div className="flex flex-col gap-4">
        <p className="text-xs text-(--color-muted)">
          You can withdraw consent at any time. Doing so immediately closes
          every active bank session and clears every active acceptance — the
          next link attempt will ask you to agree to the current terms again.
        </p>

        {err && <p className="text-xs text-(--color-negative)">{err}</p>}

        {revokeResult && (
          <p className="text-xs text-(--color-positive)">
            Consent withdrawn — {revokeResult.acceptances_revoked} acceptance(s)
            cleared, {revokeResult.connections_revoked} bank connection(s)
            closed.
          </p>
        )}

        <div>
          <Button
            variant="destructive"
            onClick={withdraw}
            loading={revokeBusy}
            disabled={active.length === 0}
          >
            Withdraw consent
          </Button>
          {active.length === 0 && history !== null && (
            <span className="ml-3 text-xs text-(--color-muted)">
              No active acceptances to withdraw.
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-hairline pt-4">
          <h3 className="text-sm font-semibold text-fg-primary">
            Acceptance history
          </h3>
          {history === null && !err && (
            <p className="text-xs text-(--color-muted)">Loading…</p>
          )}
          {history && history.length === 0 && (
            <p className="text-xs text-(--color-muted)">
              No acceptance records yet.
            </p>
          )}
          {history && history.length > 0 && (
            <ul className="flex flex-col gap-2">
              {history.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-0.5 rounded-md bg-sunken p-3 text-xs"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-mono text-fg-primary">
                      v{row.terms_version}
                    </span>
                    <span className="text-(--color-muted)">
                      accepted {shortDate(row.accepted_at)}
                    </span>
                    {row.revoked_at && (
                      <span className="text-(--color-negative)">
                        revoked {shortDate(row.revoked_at)}
                      </span>
                    )}
                    {!row.revoked_at && (
                      <span className="text-(--color-positive)">active</span>
                    )}
                  </div>
                  <div className="text-(--color-muted)">
                    {row.ip && <span className="mr-3">ip: {row.ip}</span>}
                    {row.user_agent && (
                      <span className="font-mono">
                        ua: {row.user_agent.slice(0, 64)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

// Backfill subsection — Enable Banking transactions over a user-picked
// interval. The backend chunks anything wider than ~89 days into multiple
// upstream requests, so a multi-year window is safe (just slower).
function BackfillRange() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const sixMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  }, []);

  const [dateFrom, setDateFrom] = useState(sixMonthsAgo);
  const [dateTo, setDateTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const spanDays = useMemo(() => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const diff = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
    return Number.isFinite(diff) && diff > 0 ? diff : 0;
  }, [dateFrom, dateTo]);

  // Each upstream window covers up to 89 days inclusive — surface the
  // expected request count so the user knows a 2-year window will hit
  // Enable Banking ~8 times.
  const windowCount = spanDays > 0 ? Math.ceil(spanDays / 89) : 0;

  async function run() {
    const c = getClient();
    if (!c) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { data, error, response } = await c.POST(
        '/api/sync/novobanco/range',
        { params: { query: { date_from: dateFrom, date_to: dateTo } } },
      );
      if (error) {
        setErr(`${response.status}: ${formatErr(error)}`);
      } else {
        setResult(data ?? null);
      }
    } finally {
      setBusy(false);
    }
  }

  const novo = result?.outcomes.find((o) => o.source === 'novobanco');

  return (
    <div className="flex flex-col gap-3 border-t border-hairline pt-4">
      <h3 className="text-sm font-semibold text-fg-primary">
        Backfill from Enable Banking
      </h3>
      <p className="text-xs text-(--color-muted)">
        Pull Bank account transactions over a date range. Idempotent — re-running
        an overlapping window won&apos;t create duplicates.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-fg-muted">From</span>
          <Input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
            className="!w-44"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-fg-muted">To</span>
          <Input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={today}
            onChange={(e) => setDateTo(e.target.value)}
            className="!w-44"
          />
        </label>
        <Button
          variant="primary"
          onClick={run}
          loading={busy}
          disabled={spanDays <= 0 || dateFrom > dateTo}
        >
          Import range
        </Button>
        {spanDays > 0 && (
          <span className="text-xs text-(--color-muted)">
            {spanDays} day{spanDays === 1 ? '' : 's'} ·{' '}
            {windowCount} upstream request{windowCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {err && <p className="text-xs text-(--color-negative)">{err}</p>}

      {novo && (
        <div className="flex flex-col gap-1 text-xs text-(--color-muted)">
          {novo.error ? (
            <span className="text-(--color-negative)">Bank account: {novo.error}</span>
          ) : novo.skipped ? (
            <span>Bank account: skipped — {novo.error ?? 'no credentials'}</span>
          ) : (
            <span>
              Fetched <span>{novo.fetched}</span> ·{' '}
              <span className="text-(--color-positive)">{novo.inserted}</span>{' '}
              new · <span>{novo.updated}</span> refreshed
              {result?.categorize && (
                <>
                  {' '}
                  · categorized{' '}
                  <span className="text-(--color-positive)">
                    {result.categorize.by_rule}
                  </span>{' '}
                  by rule ·{' '}
                  <span className="text-(--color-accent)">
                    {result.categorize.by_ai}
                  </span>{' '}
                  by AI
                </>
              )}
            </span>
          )}
          {novo.warning && (
            <span className="text-(--color-neutral)">
              Bank account: {novo.warning}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ConnectionRow({ conn, onRevoke }: { conn: BankConn; onRevoke: () => void }) {
  const accountCount = conn.accounts?.length ?? 0;
  const expires = conn.valid_until ? shortDate(conn.valid_until) : null;
  return (
    <li className="flex flex-col gap-1 rounded-md bg-sunken p-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-fg-primary">
          {conn.aspsp_name}{' '}
          <span className="font-normal text-fg-muted">
            ({conn.aspsp_country} · {conn.psu_type})
          </span>
        </span>
        <button
          onClick={onRevoke}
          className="text-xs text-fg-muted hover:text-negative"
        >
          Disconnect
        </button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-(--color-muted)">
        <span>{accountCount} account(s)</span>
        {expires && <span>consent expires {expires}</span>}
        {conn.last_synced_at && <span>last synced {shortDate(conn.last_synced_at)}</span>}
      </div>
      {accountCount > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5 text-xs font-mono text-(--color-muted)">
          {conn.accounts!.map((a, i) => {
            const acc = a as Record<string, unknown>;
            const iban = typeof acc.iban === 'string' ? acc.iban : '?';
            const name = typeof acc.name === 'string' ? acc.name : '';
            return (
              <li key={`${conn.id}-${i}`}>
                {iban}
                {name ? ` · ${name}` : ''}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

// --- CSV import (source picker is INSIDE the card now) -------------------

function CsvImportCard() {
  const [source, setSource] = useState<CsvSource>('novobanco');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload() {
    if (!file) return;
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    setResult(null);

    // multipart upload bypasses the openapi-fetch client (which assumes JSON
    // bodies). Re-implement the auth+url plumbing directly here.
    const url = `${getApiUrl()}/api/imports?source=${encodeURIComponent(source)}`;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const body = await resp.json();
      if (!resp.ok) {
        setError(typeof body?.detail === 'string' ? body.detail : JSON.stringify(body));
      } else {
        setResult(body);
        setFile(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Upload CSV" hint="best-effort — banks change layouts">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-(--color-muted)">
          Fallback for sources without a live integration, or to backfill
          history from before you connected. Re-uploading the same file is
          safe — duplicates dedupe on{' '}
          <code className="font-mono">hash(source + external_id)</code>.
        </p>

        <label className="flex max-w-xs flex-col gap-1 text-sm">
          <span className="font-medium text-fg-primary">CSV format</span>
          <Select
            value={source}
            onChange={(e) => {
              setSource(e.target.value as CsvSource);
              setFile(null);
              setResult(null);
              setError(null);
            }}
          >
            <option value="novobanco">Bank account (PT, ; separator)</option>
            <option value="revolut">Revolut (Statements export)</option>
            <option value="paypal">PayPal (Activity export)</option>
          </Select>
        </label>

        <DropZone
          file={file}
          onFile={(f) => {
            setFile(f);
            setResult(null);
            setError(null);
          }}
        />

        <div className="flex items-center justify-end gap-3">
          {error && <span className="mr-auto text-xs text-negative">{error}</span>}
          <Button variant="primary" onClick={upload} disabled={!file} loading={busy}>
            Upload
          </Button>
        </div>

        {result && (
          <div className="grid grid-cols-3 gap-4 border-t border-hairline pt-2 text-sm">
            <Stat label="parsed" value={result.parsed} />
            <Stat label="inserted" value={result.inserted} tone="positive" />
            <Stat label="updated" value={result.updated} />
          </div>
        )}
      </div>
    </Card>
  );
}

function DropZone({
  file,
  onFile,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={
        'rounded-lg border-2 border-dashed py-10 text-center transition-colors ' +
        (hover ? 'bg-hero-wash' : 'border-hairline')
      }
      style={hover ? { borderColor: 'var(--hero-start)' } : undefined}
    >
      {file ? (
        <div className="flex flex-col gap-1">
          <span className="font-mono font-medium text-fg-primary">{file.name}</span>
          <span className="text-xs text-fg-muted">
            {(file.size / 1024).toFixed(1)} KB
          </span>
          <button
            onClick={() => onFile(null)}
            className="mt-2 self-center text-xs text-fg-muted hover:text-negative"
          >
            remove
          </button>
        </div>
      ) : (
        <>
          <p className="mb-3 text-sm text-fg-muted">Drop a CSV here or</p>
          <label className="pressable inline-flex h-10 cursor-pointer items-center justify-center rounded-full bg-surface px-5 text-sm font-semibold text-fg-primary shadow-sm hover:shadow-md">
            Choose file
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'positive' }) {
  const cls = tone === 'positive' ? 'text-(--color-positive)' : '';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-(--color-muted)">{label}</span>
      <span className={`text-2xl font-semibold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

function formatErr(err: unknown): string {
  if (err && typeof err === 'object' && 'detail' in err) {
    const d = (err as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
  }
  return JSON.stringify(err);
}
